import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { productsData } from '@data/productsData';

// ─── Assets ────────────────────────────────────────────────────────
import coreBankingImg from '@assets/corebanking.png';
import erpSystemsImg from '@assets/erpystems.png';
import paymentsImg from '@assets/bankintegration.png';
import healthcareImg from '@assets/hospitalmanagement.png';
import educationImg from '@assets/healthcaresystems.png';
import hrImg from '@assets/hrpayroll.png';
import mobileImg from '@assets/mobilebanking.png';
import aiImg from '@assets/aitraining.png';
import integrationImg from '@assets/bankintegration.png';

// ─── Constants ────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'ALL', label: 'All' },
  { id: 'Core Banking', label: 'Core Banking' },
  { id: 'ERP', label: 'ERP' },
  { id: 'Payments', label: 'Payments' },
  { id: 'Healthcare', label: 'Healthcare' },
  { id: 'Education', label: 'Education' },
  { id: 'HR', label: 'HR' },
  { id: 'Mobile', label: 'Mobile' },
  { id: 'Integration', label: 'Integration' },
  { id: 'AI', label: 'AI' },
  { id: 'Custom', label: 'Custom' },
];

const CATEGORY_IMAGES = {
  'Core Banking': coreBankingImg,
  'ERP': erpSystemsImg,
  'Payments': paymentsImg,
  'Healthcare': healthcareImg,
  'Education': educationImg,
  'HR': hrImg,
  'Mobile': mobileImg,
  'AI': aiImg,
  'Integration': integrationImg,
  'Custom': aiImg,
};

const DEFAULT_IMAGE = coreBankingImg;
const ITEMS_PER_PAGE = 9;

// ─── Subcomponents ───────────────────────────────────────────────

const ProductCard = ({ product, onLearnMore, onDemo, index }) => {
  const productSlug = product.slug || product.id;
  const imageUrl = CATEGORY_IMAGES[product.category] || DEFAULT_IMAGE;

  const handleCardClick = () => {
    onLearnMore(productSlug);
  };

  const handleDemoClick = (e) => {
    e.stopPropagation();
    onDemo();
  };

  const handleLearnMoreClick = (e) => {
    e.stopPropagation();
    onLearnMore(productSlug);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleCardClick}
      className="group bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300 border border-slate-100 hover:border-blue-200/50 h-full flex flex-col cursor-pointer active:scale-[0.98]"
    >
      {/* Image Section */}
      <div className="relative h-44 sm:h-48 md:h-52 w-full overflow-hidden bg-slate-50 flex-shrink-0">
        <img
          src={imageUrl}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[9px] sm:text-[10px] px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-semibold shadow-sm border border-white/20">
          {product.category}
        </span>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/5 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/5 transition-colors duration-300" />
      </div>

      {/* Card Content */}
      <div className="p-3 sm:p-4 md:p-5 flex flex-col flex-1">
        {/* Title */}
        <h3 className="font-bold text-base sm:text-lg text-slate-900 leading-snug group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
          {product.name}
        </h3>
        
        {/* Description */}
        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed mt-1 flex-1 line-clamp-2 sm:line-clamp-3">
          {product.description}
        </p>

        {/* Industry Tags */}
        <div className="flex flex-wrap gap-1 mt-2 sm:mt-3">
          {product.industries?.slice(0, 3).map((industry, i) => (
            <span 
              key={i} 
              className="text-[8px] sm:text-[10px] bg-slate-50 text-slate-600 px-1.5 py-0.5 sm:px-2.5 sm:py-0.5 rounded-full border border-slate-100 font-medium"
            >
              {industry}
            </span>
          ))}
          {product.industries && product.industries.length > 3 && (
            <span className="text-[8px] sm:text-[10px] bg-slate-50 text-slate-600 px-1.5 py-0.5 sm:px-2.5 sm:py-0.5 rounded-full border border-slate-100 font-medium">
              +{product.industries.length - 3}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 sm:gap-3 mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={handleLearnMoreClick}
            className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1 group-hover:gap-2 cursor-pointer"
          >
            Learn more <FaArrowRight className="text-[10px] sm:text-xs transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            onClick={handleDemoClick}
            className="text-slate-500 hover:text-blue-600 text-xs sm:text-sm font-medium transition-colors ml-auto cursor-pointer"
          >
            Demo
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CategoryTabs = ({ categories, selected, onChange }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 160;
      const newScrollLeft = scrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, []);

  return (
    <div className="relative flex-1 w-full">
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-1.5 rounded-full shadow-md hover:shadow-lg transition-shadow"
          aria-label="Scroll categories left"
        >
          <FaChevronLeft className="text-slate-400 text-xs sm:text-sm" />
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-1 overflow-x-auto scrollbar-hide px-4 sm:px-6 py-1"
        role="tablist"
      >
        {categories.map((category) => {
          const isActive = selected === category.id;
          return (
            <button
              key={category.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(category.id)}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 touch-manipulation ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {category.label}
            </button>
          );
        })}
      </div>

      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-1.5 rounded-full shadow-md hover:shadow-lg transition-shadow"
          aria-label="Scroll categories right"
        >
          <FaChevronRight className="text-slate-400 text-xs sm:text-sm" />
        </button>
      )}
    </div>
  );
};

const EmptyState = ({ searchTerm, selectedCategory }) => {
  const getMessage = () => {
    if (searchTerm) return `No products match "${searchTerm}"`;
    if (selectedCategory !== 'ALL') return `No products in ${selectedCategory}`;
    return 'No products found';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-16 sm:py-24 bg-white rounded-2xl border border-slate-100"
    >
      <div className="text-4xl sm:text-5xl mb-4" aria-hidden="true">🔍</div>
      <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1.5 px-4">{getMessage()}</h3>
      <p className="text-slate-500 text-sm px-4">Try adjusting your search or filter criteria.</p>
    </motion.div>
  );
};

// ─── Main Component ──────────────────────────────────────────────

export default function Products() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'ALL'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) setSelectedCategory(category);
  }, [searchParams]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return productsData.filter((product) => {
      const matchesSearch = 
        product.name.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term) ||
        product.category.toLowerCase().includes(term) ||
        (product.integrations && product.integrations.some(i => i.toLowerCase().includes(term)));
      const matchesCategory = 
        selectedCategory === 'ALL' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    if (category === 'ALL') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  }, [setSearchParams]);

  const handleClearSearch = useCallback(() => setSearchTerm(''), []);
  const handleClearFilter = useCallback(() => handleCategoryChange('ALL'), [handleCategoryChange]);
  const handleLearnMore = useCallback((slug) => navigate(`/products/${slug}`), [navigate]);
  const handleDemo = useCallback(() => navigate('/demo'), [navigate]);
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Mobile-optimized pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex justify-center items-center gap-1 mt-8 sm:mt-10"
      >
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2.5 sm:px-3.5 py-1.5 rounded-lg border border-slate-200 text-xs sm:text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
          aria-label="Previous page"
        >
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">←</span>
        </button>
        
        <div className="flex gap-0.5 sm:gap-1">
          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors touch-manipulation"
              >
                1
              </button>
              {startPage > 2 && <span className="text-slate-400 self-center px-1">…</span>}
            </>
          )}
          
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                page === currentPage
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {page}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="text-slate-400 self-center px-1">…</span>}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors touch-manipulation"
              >
                {totalPages}
              </button>
            </>
          )}
        </div>
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2.5 sm:px-3.5 py-1.5 rounded-lg border border-slate-200 text-xs sm:text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <span className="sm:hidden">→</span>
        </button>
      </motion.div>
    );
  };

  return (
    <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white min-h-screen">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 sm:mb-10 md:mb-12"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-slate-900 tracking-tight">
            Our <span className="font-semibold text-blue-600">Products</span>
          </h1>
          <p className="text-slate-500 mt-1.5 sm:mt-2 max-w-xl mx-auto text-xs sm:text-sm px-2">
            Enterprise software solutions built for African organizations
          </p>
        </motion.div>

        {/* Search & Filter */}
        <div className="mb-6 sm:mb-8 md:mb-10">
          <div className="flex flex-col gap-2 sm:gap-3">
            
            {/* Search Input - Full width on mobile */}
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 pl-8 sm:pl-10 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all bg-white hover:bg-slate-50 text-sm placeholder:text-slate-400 touch-manipulation"
                aria-label="Search products"
              />
              <FaSearch className="absolute left-2.5 sm:left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs sm:text-sm" />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1 touch-manipulation"
                  aria-label="Clear search"
                >
                  <FaTimes className="text-xs sm:text-sm" />
                </button>
              )}
            </div>

            {/* Category Tabs - Full width with horizontal scroll */}
            <CategoryTabs 
              categories={CATEGORIES}
              selected={selectedCategory}
              onChange={handleCategoryChange}
            />
          </div>

          {/* Results Meta */}
          <div className="flex flex-wrap justify-between items-center mt-3 sm:mt-4 gap-1.5 sm:gap-2">
            <p className="text-xs sm:text-sm text-slate-600">
              <span className="font-medium text-slate-800">{filteredProducts.length}</span> 
              <span className="hidden xs:inline"> product{filteredProducts.length !== 1 && 's'}</span>
              {selectedCategory !== 'ALL' && (
                <span className="ml-1 text-blue-600 font-medium">in {selectedCategory}</span>
              )}
            </p>
            <div className="flex items-center gap-2 sm:gap-3">
              {selectedCategory !== 'ALL' && (
                <button
                  onClick={handleClearFilter}
                  className="text-xs sm:text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-0.5 sm:gap-1 font-medium touch-manipulation"
                >
                  <FaTimes className="text-[10px] sm:text-xs" /> <span className="hidden xs:inline">Clear filter</span>
                </button>
              )}
              {totalPages > 1 && (
                <span className="text-xs sm:text-sm text-slate-400 hidden sm:inline">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <AnimatePresence mode="wait">
          {filteredProducts.length === 0 ? (
            <EmptyState searchTerm={searchTerm} selectedCategory={selectedCategory} />
          ) : (
            <>
              <motion.div
                key={currentPage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5"
              >
                {paginatedProducts.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={index}
                    onLearnMore={handleLearnMore}
                    onDemo={handleDemo}
                  />
                ))}
              </motion.div>

              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}